import { ItemEntity, RawItem } from './Types';
import { MerchantInfoEntity } from '../MerchantInfo/Types';

import { BaseModel } from '../BaseModel/BaseModel';
import { GalleryEntity } from '../Gallery/Types';
import { PivotEntity } from '../Pivot/Types';
import { PivotModel } from '../Pivot/PivotModel';
import { GalleryModel } from '../Gallery/GalleryModel';
import { ItemsPayload } from '../../Processes/Gateways/CustomProjects/Interfaces/ProjectAddItemPayload';
import { ProjectItemOrderedQtyPayload } from '../../Processes/Gateways/CustomProjects/Interfaces/ProjectItemOrderedQtyPayload';
import { MerchantInfoModel } from '@entities/MerchantInfo/MerchantInfoModel';

export class ItemModel
    extends BaseModel<RawItem, ItemEntity>
    implements ItemEntity
{
    admined: boolean = false;
    createdAt: string = new Date().toISOString();
    deletedAt: string | null = null;
    merchantInfo: MerchantInfoEntity = new MerchantInfoModel();
    id: number = 0;
    orderedQty?: number;
    packaging: Record<string, number> | string[] = {};
    percentage?: number = 0;
    price: number | null = null;
    qty: number = 0;
    qtyFromStore: number | string = 0;
    title: string = '';
    unit: string = '';
    updatedAt: string = '';
    itemUuid?: string;
    galleries?: GalleryEntity[];
    pivot?: PivotEntity;

    constructor(data?: RawItem) {
        super();
        if (data) {
            this.initialize(data);
        }
    }

    initialize(data: RawItem) {
        this.admined = data.admined;
        this.createdAt = data.created_at;
        this.deletedAt = data.deleted_at;
        this.id = data.id;
        this.orderedQty = data.ordered_qty;
        this.packaging = data.packaging;
        this.percentage = data.percentage;
        this.price = data.price ? Number(data.price) : null;
        this.qty = data.qty ?? 0;
        this.qtyFromStore = data.qty_from_store;
        this.title = data.title;
        this.unit = data.unit;
        this.updatedAt = data.updated_at;
        this.merchantInfo = new MerchantInfoModel(data.merchant_info);
        this.itemUuid = data.uuid;
        this.galleries = data.galleries?.length
            ? data.galleries.map(gallery => new GalleryModel(gallery))
            : [];
        this.pivot = data.pivot ? new PivotModel(data.pivot) : undefined;
    }

    parse(): RawItem {
        return {
            admined: this.admined,
            created_at: this.createdAt,
            deleted_at: this.deletedAt,
            id: this.id,
            merchant_info: this.merchantInfo.parse(),
            ordered_qty: this.orderedQty,
            packaging: this.packaging,
            price: this.price ? this.price.toString() : null,
            qty: this.qty,
            qty_from_store: this.qtyFromStore,
            title: this.title,
            unit: this.unit,
            updated_at: this.updatedAt,
            ...(this.galleries?.length && {
                galleries: this.galleries.map(gallery => gallery.parse()),
            }),
            ...(this.pivot && { pivot: this.pivot.parse() }),
            ...(this.percentage && { percentage: this.percentage }),
            ...(this.itemUuid && { uuid: this.itemUuid }),
        };
    }

    updateQuantity(qty: number) {
        this.qty = qty;
    }

    getAddItemPayload(): ItemsPayload {
        return {
            id: this.id,
            qty: this.qty,
        };
    }

    updateQuantityFromItems(items: ItemEntity[]) {
        const currentItem = items.find(item => item.id === this.id);

        if (currentItem && currentItem.qty !== undefined) {
            this.updateQuantity(currentItem.qty);
        }
    }

    getOrderedQtyPayload(): ProjectItemOrderedQtyPayload {
        return {
            item_id: this.id,
            ordered_qty: this.qty,
        };
    }
}
